import React, { useContext, useEffect } from 'react';
import { ConfigContext } from '../../../../Contexts/ConfigContext';
import CheckboxInput from '../../../../Components/CheckBoxInput';
import DropDownSingle from '../../../../Components/DropDownSingle';
import PageHeading from '../../../../Components/PageHeading';
import SwitchInput from '../../../../Components/SwitchInput';
import InputConfig from '../../../../Components/InputConfig';

//mui imports====
import { Box } from '@mui/system';
import { Divider, Grid } from '@mui/material';
import useCommonStyles from '../../../../commonStyles';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

//===============
// Helper to generate config keys
const getHBDecouplingConfigKeys = (keyData) => ({
	sectionSplitEnabledKey: `${keyData}.sectionSplit.enabled`,
	sectionSplitSplitPercentageKey: `${keyData}.sectionSplit.splitPercentage`,
	enabledKey: `${keyData}.enabled`,
	variationKey: `${keyData}.variation`,
	hbDecouplingConfigDefaults: {
		[`${keyData}.sectionSplit.enabled`]: false,
		[`${keyData}.sectionSplit.splitPercentage`]: 0,
		[`${keyData}.enabled`]: false,
		[`${keyData}.variation`]: 'default',
	}
});

// Function to set default values in a config object
const setHBDecouplingConfigDefaults = (config, keyData) => {
	const { hbDecouplingConfigDefaults } = getHBDecouplingConfigKeys(keyData);
	Object.entries(hbDecouplingConfigDefaults).forEach(([key, value]) => {
		if (config[key] === undefined) {
			config[key] = value;
		}
	});
	return config;
};

const HBDecouplingSectionSplit = ({ keyData }) => {
	const { rawConfig, updateRawConfig } = useContext(ConfigContext);
	const { sectionSplitEnabledKey, sectionSplitSplitPercentageKey } = getHBDecouplingConfigKeys(keyData);
	const isSectionSplitEnabled = !!rawConfig[sectionSplitEnabledKey];
	return (
		<Grid
			container
			alignItems="center"
			spacing={6}
			sx={{ mb: '3rem', mt: '0rem', ml: '2rem', width: '91%' }}
		>
			<Grid item>
				<SwitchInput
					keyData={sectionSplitEnabledKey}
					label="Enable Internal split"
					defaultChecked={isSectionSplitEnabled}
					object={rawConfig}
					updateObject={updateRawConfig}
					containerStyles={{ width: 'fit-content' }}
				/>
			</Grid>

			<Grid item>
				<InputConfig
					containerStyles={{ width: '26rem' }}
					label="Split Percentage"
					keyData={sectionSplitSplitPercentageKey}
					inputType="number"
					object={rawConfig}
					updateObject={updateRawConfig}
					editable={isSectionSplitEnabled}
					placeholder="0% - 100%"
					inputProps={{ min: 0, max: 100 }}
					onChangeCheck={(val) => val >= 0 && val <= 100}
				/>
			</Grid>
		</Grid>
	);
};

const VARIATIONS = {
	DEFAULT: 'default',
	AUCTION_OPTIMIZATION: 'auction_optimization'
};

const HBDecoupling = ({ keyData, featureDescription }) => {
	const commonStyleClasses = useCommonStyles();
	const { rawConfig, updateRawConfig } = useContext(ConfigContext);
	const { enabledKey, variationKey } = getHBDecouplingConfigKeys(keyData);
	const isHBDecouplingEnabled = rawConfig[enabledKey];
	const selectedVariation = rawConfig[variationKey];

	useEffect(() => {
		if (isHBDecouplingEnabled) {
			setHBDecouplingConfigDefaults(rawConfig, keyData);
		}
	}, []); // intentionally run once on mount - To handle cases where toggle is already enabled but defaults are not set

	/**
	 * @name updateHBDecouplingVariation
	 * @description Updates the `variation` property in the `hbDecoupling` configuration based on the selected values from the dropdown.
	 * This function ensures that only one variation is selected at a time and updates the configuration accordingly.
	 * @param {string[]} selectedValues - Array of selected variation strings (e.g., "DEFAULT", "AUCTION OPTMIZATION")
	 */
	const updateHBDecouplingVariation = (selectedVariation) => {
		const deepCloneRawConfig = structuredClone(rawConfig);
		updateRawConfig({
			...deepCloneRawConfig,
			[`${keyData}.variation`]: selectedVariation
		});
	};

	const handleEnabledToggle = (updateFn) => {
		return (updater) => {
			setHBDecouplingConfigDefaults(rawConfig, keyData);
			updateFn(updater);
		};
	};

	return (
		<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
			<PageHeading
				headingName="HB Decoupling"
				headingPath={['Dashboard', 'Framerate', 'Experiments', 'HB Decoupling']}
				description="Manage HB Decoupling Configuration"
				showInfoButton={true}
				infoMessage={featureDescription}
			/>
			<HBDecouplingSectionSplit keyData={keyData} />
			<Divider style={{ width: '92%', marginLeft: '3%', marginTop: '5rem' }} />
			<Grid container spacing={1} style={{ marginTop: '2rem', marginLeft: '4rem', width: '94%' }}>
				<Box className={commonStyleClasses.basicOutlinedBox} style={{ width: '100%' }}>
					<Typography fontWeight={600} variant="h4">
						HB Decoupling
					</Typography>
					
					<Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
						Enabling this feature would decouple HB and Adx auctions and run them separately so that Adx request does not wait for HB Auction to complete and requests wil go on.
					</Typography>
					<Grid container spacing={0} style={{ marginTop: '4rem', marginLeft: '5rem', width: '90%' }}>
						<Grid item xs={4} style={{ marginTop: '3rem' }}>
							<CheckboxInput
								keyData={enabledKey}
								label="Enabled"
								disabled={false}
								defaultChecked={false}
								object={rawConfig}
								updateObject={handleEnabledToggle(updateRawConfig)}
							/>
						</Grid>
						<Grid item xs={4}>
							<DropDownSingle
								keyData={variationKey}
								label="Variation"
								isObjectEditor={false}
								list={Object.values(VARIATIONS)}
								directValue={selectedVariation}
								disabled={!isHBDecouplingEnabled}
								updateDirectValue={updateHBDecouplingVariation}
							/>
						</Grid>
					</Grid>
				</Box>
			</Grid>
		</Paper>
	);
};

export default HBDecoupling;
